import React, { useEffect, useMemo, useState, memo, useRef, useCallback } from 'react'

const computeWordTimings = (wordFeedback, ctm) => {
  let ctmIndex = 0;

  return wordFeedback.map((w) => {
    if (w.miscueLabel === "d") {
      return { ...w, timing: null };
    }

    const wordCount =
      w.decodedWord?.trim().split(/[\s_]+/).filter(Boolean).length || 1;

    const first = ctm[ctmIndex];
    const last = ctm[ctmIndex + wordCount - 1];

    let timing = null;

    if (first && last) {
      timing = {
        s: parseFloat(first.s),
        e: parseFloat(last.e),
      };
    }

    ctmIndex += wordCount;

    return { ...w, timing };
  });
};

const pauseStyles = {
  c: "green",
  i: "red",
  d: "#3E008E",
};

const renderWord = (w, showSubstitutionDecodedWord) => {
  switch (w.miscueLabel) {
    case "c":
      return <span style={{ color: "green" }}>{w.promptWord}</span>;
    case "i":
      return <ins>{w.decodedWord}</ins>;
    case "s":
      if(showSubstitutionDecodedWord) return <span style={{ color: "#3E008E" }}>{w.decodedWord}({w.promptWord})</span>;
      else return <span style={{ color: "#3E008E" }}>{w.promptWord}</span>;
    case "d":
      return <del>{w.promptWord}</del>;
    default:
      return null;
  }
};

const Word = memo(({ w, isActive, onClick, showSubstitutionDecodedWord}) => {
  return (
    <>
      {" "}
      <span
        className = {`word ${w.timing ? "clickable" : ""} ${isActive ? "active" : ""}`} // turns the cursor to pointer and enables hover background with css
        onClick={onClick}
      >
        {renderWord(w, showSubstitutionDecodedWord)}
      </span>

      {w.pbLabel && " "}
      {w.pbLabel && pauseStyles[w.pbLabel] && (
        <span style={{ color: pauseStyles[w.pbLabel], fontWeight: "bold" }}>
          {"||"}
        </span>
      )}
    </>
  );
});

const TOLERANCE = 0.05;

const WordPlayback = ({ item, i, audioRef, showSubstitutionDecodedWord = false }) => {

  const [activeIndex, setActiveIndex] = useState(-1);
  const rafRef = useRef(null);
  const activeIndexRef = useRef(-1);
  const wordsRef = useRef(null);

  const wordsWithTiming = useMemo(() => {
    return computeWordTimings(item.wordFeedback, item.ctm);
  }, [item.wordFeedback, item.ctm]);

  useEffect(() => {
    wordsRef.current = wordsWithTiming;
  }, [wordsWithTiming]);

  useEffect(() => {
    const audio = audioRef.current[i];
    if (!audio) return; // audio not mounted yet, skip

    const loop = () => {
      const words = wordsRef.current;
      if (!words) {
        // words not ready yet, keep looping until they are
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      const t = audio.currentTime;
      let newIndex = -1;

      // traverse from end to find the last word whose timing window contains t
      for (let k = words.length - 1; k >= 0; k--) {
        const w = words[k];
        if (!w.timing) continue; // skip deleted words (miscueLabel === "d")
        if (t >= w.timing.s - TOLERANCE && t <= w.timing.e + TOLERANCE) {
          newIndex = k;
          break;
        }
      }

      // only call setActiveIndex if the active word actually changed — avoids unnecessary re-renders
      if (newIndex !== activeIndexRef.current) {
        activeIndexRef.current = newIndex;
        setActiveIndex(newIndex);
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    const startLoop = () => {
      if (rafRef.current) return; // already running, don't start a second loop
      rafRef.current = requestAnimationFrame(loop);
    };

    const stopLoop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null; // clear ref so startLoop knows it's safe to restart
      }
    };

    const handleEnded = () => {
      stopLoop();
      // clear highlight when audio finishes naturally
      activeIndexRef.current = -1;
      setActiveIndex(-1);
    };

    // start/stop loop in sync with audio playback state
    audio.addEventListener('play', startLoop);
    audio.addEventListener('pause', stopLoop);
    audio.addEventListener('ended', handleEnded);

    return () => {
      // cleanup listeners and cancel any running loop when effect re-runs or component unmounts
      audio.removeEventListener('play', startLoop);
      audio.removeEventListener('pause', stopLoop);
      audio.removeEventListener('ended', handleEnded);
      stopLoop();
    };
  }, [i, audioRef]);

  const seekTo = useCallback((w, index, shouldPlay) => {
    if (!w.timing) return;

    const audio = audioRef.current[i];
    if (!audio) return;

    audio.currentTime = w.timing.s;
    setActiveIndex(index);
    activeIndexRef.current = index;

    if (shouldPlay && audio.paused) {
      audio.play();
    }
  }, [i, audioRef]);

  const seekHandlers = useMemo(() =>
    wordsWithTiming.map((w, j) => (e) => seekTo(w, j, e.metaKey || e.ctrlKey))
  , [wordsWithTiming, seekTo]);

  return (
    <>
      {wordsWithTiming.map((w, j) => (
        <Word
          key={j}
          w={w}
          isActive={j===activeIndex}
          onClick={seekHandlers[j]}
          showSubstitutionDecodedWord={showSubstitutionDecodedWord}
        />
      ))}
    </>
  );
};

export default WordPlayback